import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { professionalsService } from '../../services/supabaseService';

const categories = [
  { value: 'lawyer', label: 'Property Lawyer', icon: 'Scale', color: '#A78BFA', bg: 'rgba(123,47,255,0.15)', border: 'rgba(123,47,255,0.4)', desc: 'Conveyancing, contract review, property disputes' },
  { value: 'photographer', label: 'Photographer', icon: 'Camera', color: '#00D4FF', bg: 'rgba(0,212,255,0.15)', border: 'rgba(0,212,255,0.4)', desc: 'Property photography, virtual tours, aerial shoots' },
  { value: 'stylist', label: 'Property Stylist', icon: 'Palette', color: '#FF6B9D', bg: 'rgba(255,107,157,0.15)', border: 'rgba(255,107,157,0.4)', desc: 'Home staging, interior styling, furniture hire' },
  { value: 'agent', label: 'Real Estate Agent', icon: 'Home', color: '#34D399', bg: 'rgba(52,211,153,0.15)', border: 'rgba(52,211,153,0.4)', desc: 'Property sales, rentals, investment advisory' },
  { value: 'mortgage', label: 'Mortgage Broker', icon: 'DollarSign', color: '#FBBF24', bg: 'rgba(251,191,36,0.15)', border: 'rgba(251,191,36,0.4)', desc: 'Home loans, refinancing, investment finance' },
];

const australianStates = [
  { value: 'NSW', label: 'New South Wales' },
  { value: 'VIC', label: 'Victoria' },
  { value: 'QLD', label: 'Queensland' },
  { value: 'SA', label: 'South Australia' },
  { value: 'WA', label: 'Western Australia' },
  { value: 'TAS', label: 'Tasmania' },
  { value: 'NT', label: 'Northern Territory' },
  { value: 'ACT', label: 'ACT' },
];

const uaeEmirates = [
  { value: 'dubai', label: 'Dubai', icon: '🏙️' },
  { value: 'abu_dhabi', label: 'Abu Dhabi', icon: '🕌' },
  { value: 'sharjah', label: 'Sharjah', icon: '🏛️' },
  { value: 'ajman', label: 'Ajman', icon: '🌊' },
  { value: 'ras_al_khaimah', label: 'Ras Al Khaimah', icon: '⛰️' },
  { value: 'fujairah', label: 'Fujairah', icon: '🏔️' },
  { value: 'umm_al_quwain', label: 'Umm Al Quwain', icon: '🌿' },
];

const UAE_CERT_SUGGESTIONS = {
  lawyer: ['RERA Certified Broker', 'Dubai Bar Association', 'DIFC Courts Practitioner', 'UAE Ministry of Justice License', 'Professional Indemnity Insurance'],
  photographer: ['Dubai Photography License', 'DCCA Membership', 'Drone Permit (GCAA)', 'Professional Indemnity Insurance', 'Trade License (DED)'],
  stylist: ['Dubai Design District Membership', 'Interior Design License (DED)', 'Professional Indemnity Insurance', 'Trade License', 'RERA Approved Contractor'],
  agent: ['RERA Certified Broker', 'DLD Registration', 'ORN (Office Registration Number)', 'BRN (Broker Registration Number)', 'Professional Indemnity Insurance'],
  mortgage: ['UAE Central Bank License', 'CBUAE Registration', 'Professional Indemnity Insurance', 'Trade License (DED)', 'RERA Approved Financier'],
};

const AU_CERT_SUGGESTIONS = {
  lawyer: ['Law Society Membership', 'Conveyancing License', 'PEXA Certification', 'Professional Indemnity Insurance'],
  photographer: ['AIPP Membership', 'Drone Pilot License (CASA)', 'Professional Indemnity Insurance', 'Public Liability Insurance'],
  stylist: ['RESA Membership', 'AIHS Membership', 'Professional Indemnity Insurance', 'Public Liability Insurance'],
  agent: ['Real Estate License', 'REIV Membership', 'Professional Indemnity Insurance', 'CPD Compliance'],
  mortgage: ['MFAA Membership', 'FBAA Membership', 'ACL License', 'Professional Indemnity Insurance'],
};

const inputStyle = {
  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(0,212,255,0.2)',
  color: '#E2E8F0', borderRadius: '10px', padding: '10px 14px',
  width: '100%', fontSize: '14px', outline: 'none',
};
const labelStyle = { display: 'block', fontSize: '13px', fontWeight: 600, color: '#A0B4C8', marginBottom: '6px' };

const JoinAsProfessional = () => {
  const navigate = useNavigate();
  const { user, signUp } = useAuth();
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    region: '', category: '', name: '', company: '', email: '', phone: '', location: '',
    serviceAreas: [], specializations: '', experience: '', startingRate: '',
    bio: '', certifications: [], website: '', instagram: '', portfolioUrl: '',
    password: '',
    reraNumber: '', brnNumber: '', ornNumber: '', tradeLicense: '', passportNumber: '',
    emiratesId: '', nationality: '', uaeDocuments: [],
  });

  const update = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const isUAE = formData?.region === 'uae';

  const toggleArea = (area) => {
    setFormData(prev => ({
      ...prev,
      serviceAreas: prev?.serviceAreas?.includes(area)
        ? prev?.serviceAreas?.filter(s => s !== area)
        : [...prev?.serviceAreas, area],
    }));
  };

  const toggleCert = (cert) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev?.certifications?.includes(cert)
        ? prev?.certifications?.filter(c => c !== cert)
        : [...prev?.certifications, cert],
    }));
  };

  const handleDocUpload = (files) => {
    const newDocs = Array.from(files)?.map(file => ({
      id: Date.now() + Math.random(),
      name: file?.name,
      size: file?.size,
      type: file?.type,
      url: URL.createObjectURL(file),
    }));
    setFormData(prev => ({ ...prev, uaeDocuments: [...prev?.uaeDocuments, ...newDocs] }));
  };

  const removeDoc = (id) => {
    setFormData(prev => ({ ...prev, uaeDocuments: prev?.uaeDocuments?.filter(d => d?.id !== id) }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      let userId = user?.id;
      // If not logged in, create account first
      if (!userId && formData?.email) {
        const password = formData?.password || Math.random()?.toString(36)?.slice(-10) + 'A1!';
        const { data: authData, error: authError } = await signUp(formData?.email, password, {
          full_name: formData?.name,
          role: 'pro'
        });
        if (authError) throw authError;
        userId = authData?.user?.id;
      }
      if (!userId) throw new Error('Could not create account. Please try again.');
      const { error: proError } = await professionalsService?.create(userId, formData);
      if (proError) throw proError;
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err?.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const totalSteps = isUAE ? 4 : 3;
  const stepLabels = isUAE
    ? ['Category', 'Profile', 'Credentials', 'Documents']
    : ['Category', 'Profile', 'Details'];

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#050A14' }}>
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'rgba(0,212,255,0.15)', border: '2px solid rgba(0,212,255,0.4)' }}>
            <Icon name="CheckCircle" size={40} color="#00D4FF" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">Application Submitted!</h1>
          <p className="mb-2" style={{ color: '#6B9EC4' }}>Thank you, <span className="text-white font-semibold">{formData?.name}</span>!</p>
          <p className="text-sm mb-4" style={{ color: '#6B9EC4' }}>Your professional profile application has been received. Our team will review your details and get back to you within 1-2 business days.</p>
          {isUAE && (
            <div className="rounded-xl p-3 mb-6 text-left" style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.15)' }}>
              <p className="text-xs" style={{ color: '#6B9EC4' }}>🇦🇪 <strong style={{ color: '#00D4FF' }}>UAE Verification:</strong> RERA/DLD credentials and uploaded documents will be verified within 3-5 business days.</p>
            </div>
          )}
          <div className="flex gap-3 justify-center">
            <button onClick={() => navigate('/professional-marketplace')} className="px-6 py-3 rounded-xl font-semibold text-sm" style={{ background: 'linear-gradient(135deg, #00D4FF, #0099CC)', color: '#000' }}>
              Browse Professionals
            </button>
            <button onClick={() => navigate('/')} className="px-6 py-3 rounded-xl font-semibold text-sm" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#A0B4C8' }}>
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>Join as a Professional - ListingLens</title></Helmet>
      <div className="min-h-screen" style={{ background: '#050A14' }}>
        <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 0 }}>
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(0,212,255,0.05) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(123,47,255,0.05) 0%, transparent 50%)' }} />
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(0,212,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.03) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>

        <nav className="sticky top-0 z-40 px-4 py-3" style={{ background: 'rgba(5,10,20,0.95)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(0,212,255,0.15)' }}>
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/')}>
                <img src="/assets/images/logo_LL-1772022913346.jpeg" alt="ListingLens logo" className="w-9 h-9 rounded-lg object-cover" style={{ border: '1px solid rgba(0,212,255,0.4)' }} />
              </button>
              <button onClick={() => navigate('/professional-marketplace')} className="flex items-center gap-1 text-sm" style={{ color: '#6B9EC4' }}>
                <Icon name="ChevronLeft" size={16} /> Find Professionals
              </button>
            </div>
            <span className="text-sm font-medium" style={{ color: '#00D4FF' }}>Join as Professional</span>
          </div>
        </nav>

        <div className="relative z-10 max-w-3xl mx-auto px-4 py-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 text-sm font-medium" style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.2)', color: '#00D4FF' }}>
              <Icon name="Briefcase" size={14} /> Professional Registration
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Join Our Professional Network</h1>
            <p className="text-sm" style={{ color: '#6B9EC4' }}>Connect with property buyers and sellers across Australia & UAE</p>
          </div>

          {/* Step Indicators */}
          {formData?.region && (
            <div className="flex items-center justify-center gap-3 mb-8">
              {Array.from({ length: totalSteps }, (_, i) => i + 1)?.map((s) => (
                <React.Fragment key={s}>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all"
                      style={{
                        background: step > s ? 'linear-gradient(135deg, #00D4FF, #0099CC)' : step === s ? 'rgba(0,212,255,0.2)' : 'rgba(255,255,255,0.05)',
                        border: step >= s ? '2px solid rgba(0,212,255,0.5)' : '2px solid rgba(255,255,255,0.1)',
                        color: step > s ? '#000' : step === s ? '#00D4FF' : '#6B9EC4',
                      }}
                    >
                      {step > s ? <Icon name="Check" size={16} /> : s}
                    </div>
                    <span className="text-xs font-medium hidden sm:block" style={{ color: step >= s ? '#00D4FF' : '#6B9EC4' }}>
                      {stepLabels?.[s - 1]}
                    </span>
                  </div>
                  {s < totalSteps && <div className="w-10 h-0.5 rounded" style={{ background: step > s ? 'rgba(0,212,255,0.5)' : 'rgba(255,255,255,0.1)' }} />}
                </React.Fragment>
              ))}
            </div>
          )}

          <div className="rounded-2xl p-6 md:p-8" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(0,212,255,0.15)' }}>

            {/* REGION SELECTION (before step 1) */}
            {!formData?.region && (
              <div>
                <h2 className="text-xl font-bold text-white mb-2">Where are you based?</h2>
                <p className="text-sm mb-6" style={{ color: '#6B9EC4' }}>Select your primary operating region to get the right registration form</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <button
                    type="button"
                    onClick={() => update('region', 'australia')}
                    className="p-6 rounded-xl text-left transition-all duration-200 hover:scale-[1.02]"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '2px solid rgba(255,255,255,0.08)' }}
                  >
                    <div className="text-4xl mb-3">🇦🇺</div>
                    <h3 className="font-bold text-white text-lg mb-1">Australia</h3>
                    <p className="text-sm" style={{ color: '#6B9EC4' }}>NSW, VIC, QLD, WA, SA, TAS, NT, ACT</p>
                    <p className="text-xs mt-2" style={{ color: '#4B6A8A' }}>Australian state licenses, PEXA, Law Society, AIPP</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => update('region', 'uae')}
                    className="p-6 rounded-xl text-left transition-all duration-200 hover:scale-[1.02]"
                    style={{ background: 'rgba(0,212,255,0.03)', border: '2px solid rgba(0,212,255,0.15)' }}
                  >
                    <div className="text-4xl mb-3">🇦🇪</div>
                    <h3 className="font-bold text-white text-lg mb-1">United Arab Emirates</h3>
                    <p className="text-sm" style={{ color: '#6B9EC4' }}>Dubai, Abu Dhabi, Sharjah & all 7 Emirates</p>
                    <p className="text-xs mt-2" style={{ color: '#4B6A8A' }}>RERA certified, DLD registered, BRN/ORN holders</p>
                  </button>
                </div>
              </div>
            )}

            {/* STEP 1: Category */}
            {formData?.region && step === 1 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">{isUAE ? '🇦🇪' : '🇦🇺'}</span>
                  <div>
                    <h2 className="text-xl font-bold text-white">What type of professional are you?</h2>
                    <button onClick={() => { update('region', ''); setStep(1); }} className="text-xs" style={{ color: '#6B9EC4' }}>← Change region</button>
                  </div>
                </div>
                <p className="text-sm mb-6" style={{ color: '#6B9EC4' }}>Select the category that best describes your services</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {categories?.map((cat) => (
                    <button
                      key={cat?.value} type="button"
                      onClick={() => update('category', cat?.value)}
                      className="p-5 rounded-xl text-left transition-all duration-200 hover:scale-[1.02]"
                      style={{
                        background: formData?.category === cat?.value ? cat?.bg : 'rgba(255,255,255,0.03)',
                        border: `2px solid ${formData?.category === cat?.value ? cat?.border : 'rgba(255,255,255,0.08)'}`,
                        boxShadow: formData?.category === cat?.value ? `0 0 20px ${cat?.bg}` : 'none',
                      }}
                    >
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ background: cat?.bg, border: `1px solid ${cat?.border}` }}>
                        <Icon name={cat?.icon} size={24} color={cat?.color} />
                      </div>
                      <h3 className="font-bold text-white mb-1">{cat?.label}</h3>
                      <p className="text-xs" style={{ color: '#6B9EC4' }}>{cat?.desc}</p>
                      {formData?.category === cat?.value && (
                        <div className="mt-3 flex items-center gap-1 text-xs font-semibold" style={{ color: cat?.color }}>
                          <Icon name="CheckCircle" size={14} /> Selected
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => formData?.category && setStep(2)}
                  disabled={!formData?.category}
                  className="w-full py-3 rounded-xl font-semibold text-sm transition-all"
                  style={{
                    background: formData?.category ? 'linear-gradient(135deg, #00D4FF, #0099CC)' : 'rgba(255,255,255,0.05)',
                    color: formData?.category ? '#000' : '#6B9EC4',
                    cursor: formData?.category ? 'pointer' : 'not-allowed',
                  }}
                >
                  Continue <Icon name="ArrowRight" size={16} className="inline ml-1" />
                </button>
              </div>
            )}

            {/* STEP 2: Profile */}
            {formData?.region && step === 2 && (
              <form onSubmit={(e) => { e?.preventDefault(); setStep(3); }}>
                <h2 className="text-xl font-bold text-white mb-2">Your Professional Profile</h2>
                <p className="text-sm mb-6" style={{ color: '#6B9EC4' }}>Tell clients about yourself and your business</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label style={labelStyle}>Full Name *</label>
                    <input type="text" required value={formData?.name} onChange={e => update('name', e?.target?.value)} placeholder="Your full name" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Company / Business Name</label>
                    <input type="text" value={formData?.company} onChange={e => update('company', e?.target?.value)} placeholder={isUAE ? 'e.g. Al Futtaim Real Estate' : 'Your company name'} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Email Address *</label>
                    <input type="email" required value={formData?.email} onChange={e => update('email', e?.target?.value)} placeholder="your@email.com" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Phone Number *</label>
                    <input type="tel" required value={formData?.phone} onChange={e => update('phone', e?.target?.value)} placeholder={isUAE ? '+971 5x xxx xxxx' : '+61 4xx xxx xxx'} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Primary Location *</label>
                    <input type="text" required value={formData?.location} onChange={e => update('location', e?.target?.value)} placeholder={isUAE ? 'e.g. Dubai Marina, Dubai' : 'e.g. Sydney, NSW'} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Years of Experience</label>
                    <select value={formData?.experience} onChange={e => update('experience', e?.target?.value)} style={inputStyle}>
                      <option value="" style={{ background: '#0D1B2A' }}>Select...</option>
                      {['1-2 years', '3-5 years', '6-10 years', '10+ years']?.map(y => (
                        <option key={y} value={y} style={{ background: '#0D1B2A' }}>{y}</option>
                      ))}
                    </select>
                  </div>
                  {isUAE && (
                    <div>
                      <label style={labelStyle}>Nationality</label>
                      <input type="text" value={formData?.nationality} onChange={e => update('nationality', e?.target?.value)} placeholder="e.g. British, Indian, Pakistani" style={inputStyle} />
                    </div>
                  )}
                  {isUAE && (
                    <div>
                      <label style={labelStyle}>Languages Spoken</label>
                      <input type="text" value={formData?.languages} onChange={e => update('languages', e?.target?.value)} placeholder="e.g. English, Arabic, Hindi" style={inputStyle} />
                    </div>
                  )}
                </div>
                <div className="mb-4">
                  <label style={labelStyle}>Professional Bio *</label>
                  <textarea required value={formData?.bio} onChange={e => update('bio', e?.target?.value)}
                    placeholder={isUAE ? 'Describe your UAE property expertise, areas of specialization, and what sets you apart...' : 'Describe your experience, expertise, and what makes you stand out...'}
                    rows={4} style={{ ...inputStyle, resize: 'none' }}
                  />
                </div>

                {/* Service Areas */}
                <div className="mb-6">
                  <label style={labelStyle}>{isUAE ? 'Emirates Coverage (select all that apply)' : 'Service Areas (select all that apply)'}</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                    {(isUAE ? uaeEmirates : australianStates)?.map((area) => (
                      <button key={area?.value} type="button" onClick={() => toggleArea(area?.value)}
                        className="py-2 px-3 rounded-lg text-sm font-medium transition-all text-left"
                        style={{
                          background: formData?.serviceAreas?.includes(area?.value) ? 'rgba(0,212,255,0.15)' : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${formData?.serviceAreas?.includes(area?.value) ? 'rgba(0,212,255,0.4)' : 'rgba(255,255,255,0.08)'}`,
                          color: formData?.serviceAreas?.includes(area?.value) ? '#00D4FF' : '#6B9EC4',
                        }}
                      >
                        {isUAE ? `${area?.icon} ${area?.label}` : area?.value}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button type="button" onClick={() => setStep(1)} className="flex-1 py-3 rounded-xl font-semibold text-sm" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#A0B4C8' }}>Back</button>
                  <button type="submit" className="flex-1 py-3 rounded-xl font-semibold text-sm" style={{ background: 'linear-gradient(135deg, #00D4FF, #0099CC)', color: '#000' }}>
                    Continue <Icon name="ArrowRight" size={16} className="inline ml-1" />
                  </button>
                </div>
              </form>
            )}

            {/* STEP 3: Credentials / Services */}
            {formData?.region && step === 3 && (
              <form onSubmit={(e) => { e?.preventDefault(); isUAE ? setStep(4) : handleSubmit(e); }}>
                <h2 className="text-xl font-bold text-white mb-2">{isUAE ? 'UAE Credentials & Licensing' : 'Services and Credentials'}</h2>
                <p className="text-sm mb-6" style={{ color: '#6B9EC4' }}>{isUAE ? 'Provide your UAE regulatory credentials and licensing details' : 'Add your specializations, rates, and credentials'}</p>

                {/* UAE-specific license fields */}
                {isUAE && (
                  <div className="rounded-xl p-5 mb-6" style={{ background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.2)' }}>
                    <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                      <Icon name="Shield" size={16} color="#00D4FF" /> UAE Regulatory Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {(formData?.category === 'agent' || formData?.category === 'lawyer') && (
                        <div>
                          <label style={labelStyle}>RERA Certificate Number *</label>
                          <input type="text" required={formData?.category === 'agent'} value={formData?.reraNumber} onChange={e => update('reraNumber', e?.target?.value)} placeholder="e.g. 12345" style={inputStyle} />
                          <p className="text-xs mt-1" style={{ color: '#4B6A8A' }}>Real Estate Regulatory Agency (RERA) certification</p>
                        </div>
                      )}
                      {formData?.category === 'agent' && (
                        <div>
                          <label style={labelStyle}>BRN (Broker Registration Number)</label>
                          <input type="text" value={formData?.brnNumber} onChange={e => update('brnNumber', e?.target?.value)} placeholder="e.g. BRN-12345" style={inputStyle} />
                          <p className="text-xs mt-1" style={{ color: '#4B6A8A' }}>Individual broker registration with DLD</p>
                        </div>
                      )}
                      {formData?.category === 'agent' && (
                        <div>
                          <label style={labelStyle}>ORN (Office Registration Number)</label>
                          <input type="text" value={formData?.ornNumber} onChange={e => update('ornNumber', e?.target?.value)} placeholder="e.g. ORN-12345" style={inputStyle} />
                          <p className="text-xs mt-1" style={{ color: '#4B6A8A' }}>Agency/office registration with DLD</p>
                        </div>
                      )}
                      <div>
                        <label style={labelStyle}>Trade License Number *</label>
                        <input type="text" required value={formData?.tradeLicense} onChange={e => update('tradeLicense', e?.target?.value)} placeholder="e.g. DED-123456" style={inputStyle} />
                        <p className="text-xs mt-1" style={{ color: '#4B6A8A' }}>Department of Economic Development license</p>
                      </div>
                      <div>
                        <label style={labelStyle}>Emirates ID Number *</label>
                        <input type="text" required value={formData?.emiratesId} onChange={e => update('emiratesId', e?.target?.value)} placeholder="784-XXXX-XXXXXXX-X" style={inputStyle} />
                      </div>
                      <div>
                        <label style={labelStyle}>Passport Number *</label>
                        <input type="text" required value={formData?.passportNumber} onChange={e => update('passportNumber', e?.target?.value)} placeholder="e.g. A12345678" style={inputStyle} />
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-4 mb-6">
                  <div>
                    <label style={labelStyle}>Specializations *</label>
                    <input type="text" required value={formData?.specializations} onChange={e => update('specializations', e?.target?.value)}
                      placeholder={isUAE ? 'e.g. Off-Plan Sales, Luxury Villas, Commercial Leasing (comma separated)' : 'e.g. Property Conveyancing, Contract Review, Strata Law (comma separated)'} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Starting Rate *</label>
                    <input type="text" required value={formData?.startingRate} onChange={e => update('startingRate', e?.target?.value)}
                      placeholder={isUAE ? 'e.g. AED 500/hr or AED 5,000/project' : 'e.g. $350/hr or $1,200/project'} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Website</label>
                    <input type="url" value={formData?.website} onChange={e => update('website', e?.target?.value)} placeholder="https://yourwebsite.com" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Instagram / Social</label>
                    <input type="text" value={formData?.instagram} onChange={e => update('instagram', e?.target?.value)} placeholder="@yourhandle" style={inputStyle} />
                  </div>
                </div>

                {/* Certifications */}
                <div className="mb-6">
                  <label style={labelStyle}>Professional Certifications & Memberships</label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(isUAE ? UAE_CERT_SUGGESTIONS?.[formData?.category] : AU_CERT_SUGGESTIONS?.[formData?.category])?.map(cert => (
                      <button
                        key={cert} type="button"
                        onClick={() => toggleCert(cert)}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                        style={{
                          background: formData?.certifications?.includes(cert) ? 'rgba(0,212,255,0.15)' : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${formData?.certifications?.includes(cert) ? 'rgba(0,212,255,0.4)' : 'rgba(255,255,255,0.08)'}`,
                          color: formData?.certifications?.includes(cert) ? '#00D4FF' : '#9CA3AF',
                        }}
                      >
                        {formData?.certifications?.includes(cert) ? '✓ ' : '+ '}{cert}
                      </button>
                    ))}
                  </div>
                  {formData?.certifications?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {formData?.certifications?.map(cert => (
                        <span key={cert} className="flex items-center gap-1 px-3 py-1 rounded-full text-xs" style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.3)', color: '#00D4FF' }}>
                          <Icon name="Award" size={10} /> {cert}
                          <button type="button" onClick={() => toggleCert(cert)} className="ml-1"><Icon name="X" size={10} /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {!isUAE && (
                  <div className="rounded-xl p-4 mb-6" style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.15)' }}>
                    <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><Icon name="Eye" size={14} color="#00D4FF" /> Profile Preview</h4>
                    <div className="space-y-1.5 text-sm">
                      <p><span style={{ color: '#6B9EC4' }}>Name:</span> <span className="text-white">{formData?.name || '-'}</span></p>
                      <p><span style={{ color: '#6B9EC4' }}>Category:</span> <span className="text-white capitalize">{formData?.category || '-'}</span></p>
                      <p><span style={{ color: '#6B9EC4' }}>Location:</span> <span className="text-white">{formData?.location || '-'}</span></p>
                      <p><span style={{ color: '#6B9EC4' }}>Rate:</span> <span className="text-white">{formData?.startingRate || '-'}</span></p>
                      <p><span style={{ color: '#6B9EC4' }}>Service Areas:</span> <span className="text-white">{formData?.serviceAreas?.join(', ') || '-'}</span></p>
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <button type="button" onClick={() => setStep(2)} className="flex-1 py-3 rounded-xl font-semibold text-sm" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#A0B4C8' }}>Back</button>
                  <button type="submit" className="flex-1 py-3 rounded-xl font-semibold text-sm" style={{ background: 'linear-gradient(135deg, #00D4FF, #0099CC)', color: '#000', boxShadow: '0 0 15px rgba(0,212,255,0.3)' }}>
                    {isUAE ? (<>Continue <Icon name="ArrowRight" size={16} className="inline ml-1" /></>) : (<><Icon name="Send" size={16} className="inline mr-2" /> Submit Application</>)}
                  </button>
                </div>
              </form>
            )}

            {/* STEP 4 (UAE only): Document Upload */}
            {formData?.region && step === 4 && isUAE && (
              <form onSubmit={handleSubmit}>
                <h2 className="text-xl font-bold text-white mb-2">Upload Required Documents</h2>
                <p className="text-sm mb-6" style={{ color: '#6B9EC4' }}>Upload your UAE regulatory documents for verification</p>

                {/* Required Documents Info */}
                <div className="rounded-xl p-4 mb-6" style={{ background: 'rgba(251,191,36,0.05)', border: '1px solid rgba(251,191,36,0.2)' }}>
                  <h4 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: '#FBBF24' }}>
                    <Icon name="AlertCircle" size={14} color="#FBBF24" /> Required Documents for UAE Registration
                  </h4>
                  <ul className="space-y-1.5">
                    {[
                      { doc: 'Passport Copy', req: 'Required', note: 'Valid passport, all pages' },
                      { doc: 'Emirates ID', req: 'Required', note: 'Front and back copy' },
                      { doc: 'Trade License', req: 'Required', note: 'Current DED/Free Zone license' },
                      ...(formData?.category === 'agent' ? [
                        { doc: 'RERA Certificate', req: 'Required', note: 'Valid RERA broker certificate' },
                        { doc: 'DLD Registration', req: 'Required', note: 'DLD broker card' },
                      ] : []),
                      { doc: 'Professional Indemnity Insurance', req: 'Recommended', note: 'Current policy document' },
                      { doc: 'Portfolio / Work Samples', req: 'Optional', note: 'Previous projects, case studies' },
                    ]?.map(item => (
                      <li key={item?.doc} className="flex items-start gap-2 text-xs">
                        <span className="mt-0.5 px-1.5 py-0.5 rounded text-xs font-semibold" style={{ background: item?.req === 'Required' ? 'rgba(239,68,68,0.15)' : item?.req === 'Recommended' ? 'rgba(251,191,36,0.15)' : 'rgba(255,255,255,0.05)', color: item?.req === 'Required' ? '#F87171' : item?.req === 'Recommended' ? '#FBBF24' : '#6B9EC4' }}>{item?.req}</span>
                        <span className="text-white font-medium">{item?.doc}</span>
                        <span style={{ color: '#6B9EC4' }}>— {item?.note}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Upload Zone */}
                <div
                  className="relative rounded-xl p-8 text-center mb-6 transition-all duration-200"
                  style={{ background: 'rgba(255,255,255,0.02)', border: '2px dashed rgba(0,212,255,0.3)' }}
                  onDragOver={e => e?.preventDefault()}
                  onDrop={e => { e?.preventDefault(); handleDocUpload(e?.dataTransfer?.files); }}
                >
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.2)' }}>
                    <Icon name="Upload" size={28} color="#00D4FF" />
                  </div>
                  <p className="text-sm font-semibold text-white mb-1">Drag & drop documents here</p>
                  <p className="text-xs mb-4" style={{ color: '#6B9EC4' }}>Passport, Emirates ID, Trade License, RERA Certificate, Insurance</p>
                  <label className="cursor-pointer px-5 py-2.5 rounded-lg text-sm font-semibold" style={{ background: 'rgba(0,212,255,0.15)', border: '1px solid rgba(0,212,255,0.3)', color: '#00D4FF' }}>
                    Browse Files
                    <input type="file" multiple accept="image/*,.pdf" className="hidden" onChange={e => handleDocUpload(e?.target?.files)} />
                  </label>
                  <p className="text-xs mt-3" style={{ color: '#4B6A8A' }}>PDF, JPG, PNG · Max 10MB per file · All documents encrypted</p>
                </div>

                {/* Uploaded Documents List */}
                {formData?.uaeDocuments?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-white mb-3">{formData?.uaeDocuments?.length} document{formData?.uaeDocuments?.length !== 1 ? 's' : ''} uploaded</h3>
                    <div className="space-y-2">
                      {formData?.uaeDocuments?.map(doc => (
                        <div key={doc?.id} className="flex items-center justify-between px-4 py-3 rounded-xl" style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.15)' }}>
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(0,212,255,0.1)' }}>
                              <Icon name={doc?.type?.startsWith('image/') ? 'Image' : 'FileText'} size={18} color="#00D4FF" />
                            </div>
                            <div>
                              <p className="text-sm text-white font-medium truncate max-w-[200px]">{doc?.name}</p>
                              <p className="text-xs" style={{ color: '#6B9EC4' }}>{(doc?.size / 1024 / 1024)?.toFixed(2)} MB</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs px-2 py-1 rounded" style={{ background: 'rgba(52,211,153,0.1)', color: '#34D399' }}>✓ Uploaded</span>
                            <button type="button" onClick={() => removeDoc(doc?.id)} className="p-1.5 rounded-lg transition-colors" style={{ background: 'rgba(239,68,68,0.1)' }}>
                              <Icon name="Trash2" size={14} color="#F87171" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Profile Preview for UAE */}
                <div className="rounded-xl p-4 mb-6" style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.15)' }}>
                  <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><Icon name="Eye" size={14} color="#00D4FF" /> Application Summary</h4>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                    <p><span style={{ color: '#6B9EC4' }}>Name:</span> <span className="text-white">{formData?.name || '-'}</span></p>
                    <p><span style={{ color: '#6B9EC4' }}>Category:</span> <span className="text-white capitalize">{formData?.category || '-'}</span></p>
                    <p><span style={{ color: '#6B9EC4' }}>Location:</span> <span className="text-white">{formData?.location || '-'}</span></p>
                    <p><span style={{ color: '#6B9EC4' }}>Rate:</span> <span className="text-white">{formData?.startingRate || '-'}</span></p>
                    {formData?.reraNumber && <p><span style={{ color: '#6B9EC4' }}>RERA:</span> <span className="text-white">{formData?.reraNumber}</span></p>}
                    {formData?.tradeLicense && <p><span style={{ color: '#6B9EC4' }}>Trade License:</span> <span className="text-white">{formData?.tradeLicense}</span></p>}
                    <p><span style={{ color: '#6B9EC4' }}>Emirates:</span> <span className="text-white">{formData?.serviceAreas?.map(a => uaeEmirates?.find(e => e?.value === a)?.label)?.join(', ') || '-'}</span></p>
                    <p><span style={{ color: '#6B9EC4' }}>Docs:</span> <span className="text-white">{formData?.uaeDocuments?.length} uploaded</span></p>
                  </div>
                </div>

                <div className="rounded-xl p-3 mb-6" style={{ background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.15)' }}>
                  <p className="text-xs" style={{ color: '#6B9EC4' }}>🔒 <strong style={{ color: '#00D4FF' }}>Secure & Confidential:</strong> All documents are encrypted and used solely for identity verification. RERA/DLD credentials will be verified with the respective authorities within 3-5 business days.</p>
                </div>

                <div className="flex gap-3">
                  <button type="button" onClick={() => setStep(3)} className="flex-1 py-3 rounded-xl font-semibold text-sm" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#A0B4C8' }}>Back</button>
                  <button type="submit" className="flex-1 py-3 rounded-xl font-semibold text-sm" style={{ background: 'linear-gradient(135deg, #00D4FF, #0099CC)', color: '#000', boxShadow: '0 0 15px rgba(0,212,255,0.3)' }}>
                    <Icon name="Send" size={16} className="inline mr-2" /> Submit Application
                  </button>
                </div>
              </form>
            )}
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: 'Users', title: 'Reach More Clients', desc: isUAE ? 'Connect with expats and investors across all 7 Emirates' : 'Connect with thousands of active property buyers and sellers' },
              { icon: 'TrendingUp', title: 'Grow Your Business', desc: 'Increase your bookings and build your professional reputation' },
              { icon: 'Shield', title: 'Verified Badge', desc: isUAE ? 'RERA-verified badge builds trust with UAE property buyers' : 'Get verified to stand out and build trust with potential clients' },
            ]?.map((b) => (
              <div key={b?.title} className="rounded-xl p-4 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,212,255,0.1)' }}>
                <Icon name={b?.icon} size={24} color="#00D4FF" className="mx-auto mb-2" />
                <h4 className="text-sm font-bold text-white mb-1">{b?.title}</h4>
                <p className="text-xs" style={{ color: '#6B9EC4' }}>{b?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default JoinAsProfessional;
